import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import SignatureCanvas from 'react-signature-canvas';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { FileSignature, CheckCircle2, Eraser } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicSignContract() {
  const { token } = useParams();
  const [contract, setContract] = useState(null);
  const [error, setError] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const sigRef = useRef(null);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/public/contracts/sign/${token}`)
      .then((res) => {
        setContract(res.data);
        setName(res.data.party_name || '');
        if (res.data.status === 'signed') setDone(true);
      })
      .catch(() => setError('This contract link is invalid or has been removed.'));
  }, [token]);

  const submit = async () => {
    if (!agreed) { toast.error('Please confirm you have read and agree to the contract'); return; }
    if (!name.trim()) { toast.error('Please enter your full name'); return; }
    if (!sigRef.current || sigRef.current.isEmpty()) { toast.error('Please sign in the signature box'); return; }
    setSubmitting(true);
    try {
      await axios.patch(`${BACKEND_URL}/api/public/contracts/sign/${token}`, {
        signer_name: name.trim(),
        signature: sigRef.current.getTrimmedCanvas().toDataURL('image/png'),
      });
      setDone(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to submit signature');
    } finally {
      setSubmitting(false);
    }
  };

  if (error) {
    return <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4"><p className="text-gray-500" data-testid="contract-error">{error}</p></div>;
  }
  if (!contract) {
    return <div className="min-h-screen flex items-center justify-center bg-gray-50"><p className="text-gray-400">Loading contract…</p></div>;
  }
  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="bg-white rounded-2xl shadow-lg p-10 text-center max-w-md" data-testid="contract-signed-confirmation">
          <CheckCircle2 size={56} className="text-green-600 mx-auto mb-4" />
          <h1 className="text-xl font-bold mb-2">Contract Signed</h1>
          <p className="text-gray-600 text-sm">Thank you. A signed copy of <strong>{contract.title} {contract.document_number}</strong> has been emailed to you and to NJK Logistics.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4" data-testid="contract-sign-page">
      <div className="max-w-3xl mx-auto">
        <div className="bg-[#5c1a1a] text-white rounded-t-2xl p-6">
          <p className="text-[#e8c87a] text-xs font-semibold uppercase tracking-[0.25em] mb-1">NJK Logistics Pte Ltd</p>
          <h1 className="text-xl font-bold flex items-center gap-2"><FileSignature size={22} /> {contract.title}</h1>
          <p className="text-sm text-white/70 mt-1">{contract.document_number} · Prepared for {contract.party_name}</p>
        </div>
        <div className="bg-white shadow-lg rounded-b-2xl p-6">
          <div className="border rounded-xl p-5 max-h-[50vh] overflow-y-auto bg-gray-50/60 mb-6" data-testid="contract-content-box">
            {contract.content.split('\n').map((line, i) => {
              const trimmed = line.trim();
              if (!trimmed) return null;
              const letters = trimmed.replace(/[^a-zA-Z]/g, '');
              const isHeader = /^\d+\.\s/.test(trimmed) || (letters.length > 3 && trimmed === trimmed.toUpperCase() && trimmed.length < 80);
              return (
                <p key={i} className={isHeader ? 'font-bold text-[#5c1a1a] mt-4 mb-1 text-sm' : 'text-sm text-gray-700 leading-relaxed mb-2'}>
                  {trimmed}
                </p>
              );
            })}
          </div>

          <label className="flex items-start gap-3 mb-5 cursor-pointer">
            <Checkbox checked={agreed} onCheckedChange={setAgreed} data-testid="contract-agree-checkbox" className="mt-0.5" />
            <span className="text-sm text-gray-700">I confirm that I have read, understood and agree to the terms of this contract.</span>
          </label>

          <div className="mb-5">
            <Label>Full Name (as per NRIC)</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="contract-signer-name" className="mt-1" />
          </div>

          <div className="mb-6">
            <div className="flex items-center justify-between mb-1">
              <Label>Signature</Label>
              <Button type="button" variant="ghost" size="sm" onClick={() => sigRef.current?.clear()} data-testid="clear-signature-btn"><Eraser size={14} className="mr-1" /> Clear</Button>
            </div>
            <div className="border-2 border-dashed border-gray-300 rounded-xl bg-white">
              <SignatureCanvas ref={sigRef} penColor="#1a1a1a" canvasProps={{ className: 'w-full h-40 rounded-xl', 'data-testid': 'contract-signature-canvas' }} />
            </div>
            <p className="text-xs text-gray-400 mt-1">Sign above using your finger or mouse.</p>
          </div>

          <Button onClick={submit} disabled={submitting} className="w-full bg-[#5c1a1a] hover:bg-[#4a1515] text-white h-12 text-base" data-testid="submit-signature-btn">
            {submitting ? 'Submitting…' : 'Sign Contract'}
          </Button>
        </div>
      </div>
    </div>
  );
}
